// ユーザー一覧の表示(Show).
import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  ForwardRefRenderFunction,
  useLayoutEffect,
  memo,
  useRef,
} from 'react';

import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import * as UserListField from '../../constants/UserListField';
import * as UserListFieldConst from '../../constants/UserListField';
import { Memo } from '../../constants/Report';

import { dispatchIdentifierToGTM } from '../../utils/commonUtil';

import GeneralPrimaryButton from '../Atoms/GeneralPrimaryButton';
import GeneralPrimaryCheckBox from '../Atoms/GeneralPrimaryCheckBox';
import GeneralPrimaryTextArea from '../Atoms/GeneralPrimaryTextArea';

import { makeStyles } from '@material-ui/core/styles';
import { Box } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  sortableHeader: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#d8d4cf',
    },
  },
  sortIcon: {
    marginLeft: '5px',
  },
  tblStyle00: {
    width: '100%',
    maxWidth: '100%',
    border: 'solid 1px #999',
    '& thead, & tbody': {
      width: '100%',
      maxWidth: '100%',
      '@media print': {
        '@supports (-ms-ime-align: auto)': {
          border: 'solid 2px #999',
        },
      },
      '& th, & td': {
        padding: '0 5px',
        textAlign: 'left',
        border: 'solid 1px #999',
      },
      '& th': {
        position: 'relative',
        // firefoxで枠線が消える対策
        backgroundClip: 'padding-box',
        backgroundColor: '#e8e4df',
        lineHeight: '1.1em',
        verticalAlign: 'middle',
        padding: '3px 0',
        fontWeight: 'normal',
        textAlign: 'center',
        '&:nth-of-type(1)': {
          maxWidth: '40px',
          '@media print': {
            display: 'none',
          },
        },
        '&:nth-of-type(2)': {
          maxWidth: '50px',
        },
        '&:nth-of-type(3)': {
          maxWidth: '150px',
        },
        '&:nth-of-type(4)': {
          maxWidth: '115px',
        },
        '&:nth-of-type(5)': {
          maxWidth: '70px',
        },
        '&:nth-of-type(6)': {
          maxWidth: '175px',
        },
        '&:nth-of-type(7)': {
          width: '230px',
        },
        '&:nth-of-type(8)': {
          width: '100px',
          '@media print': {
            display: 'none',
          },
        },
      },
      '& td': {
        height: '50px',
        lineHeight: '1.1em',
        verticalAlign: 'middle',
        wordBreak: 'break-word',
        '&:nth-of-type(1)': {
          textAlign: 'center',
          padding: '6px 5px 0px 5px',
          '@media print': {
            display: 'none',
          },
        },
        '&:nth-of-type(2)': {
          textAlign: 'center',
          padding: '3px 5px',
        },
        '&:nth-of-type(3)': {
          textAlign: 'center',
          padding: '3px 5px',
        },
        '&:nth-of-type(4)': {
          fontSize: '90%',
          textAlign: 'center',
          padding: '3px 5px',
        },
        '&:nth-of-type(5)': {
          fontSize: '90%',
          textAlign: 'center',
          padding: '3px 5px',
        },
        '&:nth-of-type(6)': {
          fontSize: '90%',
          textAlign: 'center',
          padding: '3px 5px',
        },
        '&:nth-of-type(7)': {
          textAlign: 'center',
          padding: '6px 5px 0px 5px',
        },
        '&:nth-of-type(8)': {
          textAlign: 'center',
          '@media print': {
            display: 'none',
          },
        },
      },
    },
  },
  mobileTableFontSize: {
    fontSize: '0.8em',
    lineHeight: '1.3em',
    overflowX: 'scroll',
    '-webkit-overflow-scrolling': 'touch',
    '@media print': {
      display: 'table',
    },
  },
  isPrintRow: {
    '@media print': {
      display: 'table-row',
    },
  },
  isNotPrintRow: {
    '@media print': {
      display: 'none',
    },
  },
  hasDataRow: {
    // backgroundColor: '#e3f2fd',
    backgroundColor: '#ffffff',
  },
  dataIndicator: {
    width: '15px',
    height: '15px',
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: '5px',
  },
  hasData: {
    backgroundColor: '#4caf50',
  },
  noData: {
    backgroundColor: '#f44336',
  },
  disabledRow: {
    backgroundColor: '#f5f5f5',
    opacity: 0.6,
    '& td': {
      color: '#999',
    },
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2),
  },
}));

export interface UserListProps {
  users: UserListField.User[];
  memos?: Memo[];
  reload?: any;
  invokedPath?: string;
  currentMonth?: string | undefined;
  isCheckBox?: boolean;
  isMemoColumn?: boolean;
  isReportCheckColumn?: boolean;
  textFormPlaceholder?: string;
  textFormMaxLength?: number;
  getLastMonthMemos?: (checkDataArr: UserListField.CheckData[]) => void;
  saveAllMemoValue?: (checkDataArr: UserListField.CheckData[]) => void;
  reportOutputButtonOnClick?: (user: UserListField.User, currentMonth?: string) => void;
  onCheckChange?: (checkedUsers: UserListField.User[]) => void;
  usersWithData?: { [key: string]: boolean }; // データを持つユーザーのマップ
  basicInfoUsers?: string[]; // 利用者基本情報に登録されているユーザーIDのリスト
  isUserInfoTab?: boolean; // 利用者情報タブが選択されているかどうか
}

export interface RefFunction {
  getCheckedUsers: () => UserListField.User[];
}

// 詳細本体.
const UserList: ForwardRefRenderFunction<RefFunction, UserListProps> = (props, ref) => {
  const {
    users,
    memos,
    invokedPath = '',
    currentMonth,
    isCheckBox = true,
    isMemoColumn = false,
    isReportCheckColumn = false,
    textFormPlaceholder = '',
    textFormMaxLength = undefined,
    /*eslint no-empty-pattern: "off"*/
    getLastMonthMemos = ([]) => null,
    saveAllMemoValue = ([]) => null,
    reportOutputButtonOnClick = ({}) => null,
    onCheckChange = () => null,
    usersWithData = {},
    basicInfoUsers = [],
    isUserInfoTab = false,
  } = props;

  // stateの準備.
  // const initialAllCheckBoxState: UserListFieldConst.AllCheckData = {
  //   indeterminate: false,
  //   selected: true,
  // };
  const initialChackDataArr = users.map((user: UserListFieldConst.User, index: number) => {
    // 利用者情報タブで、basicInfoに登録されていないユーザーの場合はチェックを外す
    const isNotInBasicInfo = isUserInfoTab && basicInfoUsers.length > 0 && !basicInfoUsers.includes(user.user_id);

    return {
      id: index,
      selected: isNotInBasicInfo ? false : true,
      userId: user.user_id,
    } as UserListFieldConst.CheckData;
  });
  // ローカルストレージからチェックボックスの状態を復元する
  const getStoredCheckData = () => {
    try {
      const storedData = localStorage.getItem('userListCheckData');
      if (storedData) {
        const parsedData = JSON.parse(storedData);
        // ユーザーIDをキーとしてチェック状態を保持するオブジェクト
        const userCheckMap: { [userId: string]: boolean } = {};
        parsedData.forEach((item: UserListFieldConst.CheckData) => {
          userCheckMap[item.userId] = item.selected;
        });

        // 現在のユーザーリストに対応するチェックデータを作成
        return users.map((user: UserListFieldConst.User, index: number) => {
          // 利用者情報タブで、basicInfoに登録されていないユーザーの場合はチェックを外す
          const isNotInBasicInfo = isUserInfoTab && basicInfoUsers.length > 0 && !basicInfoUsers.includes(user.user_id);
          const defaultChecked = isNotInBasicInfo ? false : true;

          return {
            id: index,
            selected: isNotInBasicInfo
              ? false
              : userCheckMap[user.user_id] !== undefined
              ? userCheckMap[user.user_id]
              : defaultChecked,
            userId: user.user_id,
          } as UserListFieldConst.CheckData;
        });
      }
    } catch (error) {
      console.error('Failed to load check data from localStorage:', error);
    }
    return initialChackDataArr;
  };

  const storedCheckData = getStoredCheckData();
  const [checkDataArr, setCheckDataArr] = useState(storedCheckData);

  // 全てのチェックボックスの状態を計算
  const calculateAllCheckState = (checkData: UserListFieldConst.CheckData[]) => {
    const selectedCount = checkData.filter((item) => item.selected).length;
    if (selectedCount === 0) {
      return { indeterminate: false, selected: false };
    } else if (selectedCount === checkData.length) {
      return { indeterminate: false, selected: true };
    } else {
      return { indeterminate: true, selected: false };
    }
  };

  const initialAllCheckState = calculateAllCheckState(storedCheckData);
  const [allCheckData, setAllcheckData] = useState(initialAllCheckState);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'ascending' | 'descending' } | null>(null);
  const memoRefs = useRef<HTMLTextAreaElement[]>([]);

  const classes = useStyles();
  const theme = useTheme();
  const matches = {
    xs: useMediaQuery(theme.breakpoints.down('xs')),
  };

  const dataNullFlg = users ? users.length : 0;

  useLayoutEffect(() => {
    if (isMemoColumn) {
      users.forEach((user: UserListFieldConst.User, index: number) => {
        const userComment = memos?.find((memo) => {
          return user.user_id === memo.user_id;
        });
        memoRefs.current[index].value = userComment ? userComment.comment : '';
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memos]);

  // ページロード時にチェックされたユーザーのリストを親コンポーネントに通知
  useLayoutEffect(() => {
    const checkedUsersList: UserListField.User[] = [];
    checkDataArr.forEach((checkbox, idx) => {
      if (checkbox.selected) {
        if (isMemoColumn && memoRefs.current[idx]) {
          const usersWithMemo = { ...users[idx], memo: memoRefs.current[idx].value };
          checkedUsersList.push(usersWithMemo);
        } else {
          checkedUsersList.push(users[idx]);
        }
      }
    });
    onCheckChange(checkedUsersList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // basicInfoUsersが更新された時にチェックボックスの状態を更新
  useLayoutEffect(() => {
    // 利用者情報タブでbasicInfoUsersが設定された場合
    if (isUserInfoTab && basicInfoUsers.length > 0) {
      const updatedCheckData = checkDataArr.map((checkData: UserListFieldConst.CheckData, index: number) => {
        const user = users[index];
        if (!user) return checkData;

        const isNotInBasicInfo = !basicInfoUsers.includes(user.user_id);

        // basicInfoに存在しない利用者のチェックを外す
        if (isNotInBasicInfo) {
          return {
            id: checkData.id,
            selected: false,
            userId: checkData.userId,
          };
        }
        return checkData;
      });

      // チェックボックスの状態を更新
      setCheckDataArr(updatedCheckData);

      // 全選択チェックボックスの状態も更新
      const newAllCheckState = calculateAllCheckState(updatedCheckData);
      setAllcheckData(newAllCheckState);

      // チェックされたユーザーのリストを親コンポーネントに通知
      const checkedUsersList: UserListField.User[] = [];
      updatedCheckData.forEach((checkbox, idx) => {
        if (checkbox.selected) {
          const user = users[idx];
          // basicInfoに存在するユーザーのみカウント
          if (basicInfoUsers.includes(user.user_id)) {
            if (isMemoColumn && memoRefs.current[idx]) {
              const usersWithMemo = { ...user, memo: memoRefs.current[idx].value };
              checkedUsersList.push(usersWithMemo);
            } else {
              checkedUsersList.push(user);
            }
          }
        }
      });
      onCheckChange(checkedUsersList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicInfoUsers, isUserInfoTab]);

  /**
   * チェックボックスがonになっているユーザーの取得
   * 親要素から呼び出す
   *
   * @memberof UserListshow
   */
  useImperativeHandle(ref, () => ({
    getCheckedUsers: () => {
      const checkedUsersList: any[] = [];

      checkDataArr.forEach((checkbox, idx: number) => {
        if (checkbox.selected === true) {
          const user = users[idx];
          // 利用者情報タブの場合、basicInfoに存在するユーザーのみを返す
          if (isUserInfoTab && basicInfoUsers.length > 0 && !basicInfoUsers.includes(user.user_id)) {
            return; // このユーザーはスキップ
          }

          if (isMemoColumn) {
            const usersWithMemo = { ...user, memo: memoRefs.current[idx].value };
            checkedUsersList.push(usersWithMemo);
          } else {
            checkedUsersList.push(user);
          }
        }
      });
      return checkedUsersList;
    },
  }));

  // チェックボックスの状況変更.
  const changeSelection = (id: number) => {
    // 利用者情報タブで、basicInfoに登録されていないユーザーの場合は何もしない
    const user = users[id];
    const isNotInBasicInfo = isUserInfoTab && basicInfoUsers.length > 0 && !basicInfoUsers.includes(user.user_id);
    if (isNotInBasicInfo) {
      return;
    }

    const nextState = checkDataArr.map((checkData: UserListFieldConst.CheckData) => {
      return {
        id: checkData.id,
        selected: checkData.id === id ? !checkData.selected : checkData.selected,
        userId: checkData.userId,
      };
    });
    const nextAllState = {
      indeterminate: true,
      selected: false,
    };
    setCheckDataArr(nextState);
    setAllcheckData(nextAllState);
    dispatchIdentifierToGTM('all_users_checkbox');

    // チェックボックスの状態をローカルストレージに保存
    localStorage.setItem('userListCheckData', JSON.stringify(nextState));

    // チェックされたユーザーのリストを親コンポーネントに通知
    const checkedUsersList: UserListField.User[] = [];
    nextState.forEach((checkbox, idx) => {
      if (checkbox.selected) {
        const user = users[idx];
        // 利用者情報タブの場合、basicInfoに存在するユーザーのみカウント
        if (isUserInfoTab && basicInfoUsers.length > 0 && !basicInfoUsers.includes(user.user_id)) {
          return; // このユーザーはスキップ
        }

        if (isMemoColumn && memoRefs.current[idx]) {
          const usersWithMemo = { ...user, memo: memoRefs.current[idx].value };
          checkedUsersList.push(usersWithMemo);
        } else {
          checkedUsersList.push(user);
        }
      }
    });
    onCheckChange(checkedUsersList);
  };

  // 全てのチェックボックスの状況変更.
  const changeAllSelection = () => {
    const nextState = checkDataArr.map((checkData: UserListFieldConst.CheckData, idx: number) => {
      // 利用者情報タブで、basicInfoに登録されていないユーザーの場合はチェックを変更しない
      const user = users[idx];
      const isNotInBasicInfo = isUserInfoTab && basicInfoUsers.length > 0 && !basicInfoUsers.includes(user.user_id);

      return {
        id: checkData.id,
        selected: isNotInBasicInfo ? false : !allCheckData.selected,
        userId: checkData.userId,
      };
    });
    const nextAllState = {
      indeterminate: false,
      selected: !allCheckData.selected,
    };
    setCheckDataArr(nextState);
    setAllcheckData(nextAllState);
    dispatchIdentifierToGTM('all_users_checkbox');

    // チェックボックスの状態をローカルストレージに保存
    localStorage.setItem('userListCheckData', JSON.stringify(nextState));

    // チェックされたユーザーのリストを親コンポーネントに通知
    const checkedUsersList: UserListField.User[] = [];
    if (!allCheckData.selected) {
      // 全選択の場合
      users.forEach((user, idx) => {
        // 利用者情報タブの場合、basicInfoに存在するユーザーのみカウント
        if (isUserInfoTab && basicInfoUsers.length > 0 && !basicInfoUsers.includes(user.user_id)) {
          return; // このユーザーはスキップ
        }

        if (isMemoColumn && memoRefs.current[idx]) {
          const usersWithMemo = { ...user, memo: memoRefs.current[idx].value };
          checkedUsersList.push(usersWithMemo);
        } else {
          checkedUsersList.push(user);
        }
      });
    }
    onCheckChange(checkedUsersList);
  };

  // ソート関数
  const requestSort = (key: string) => {
    let direction: 'ascending' | 'descending' = 'descending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'descending') {
      direction = 'ascending';
    }
    setSortConfig({ key, direction });
  };

  // ソートされたユーザーリストを取得
  const getSortedUsers = () => {
    if (!sortConfig) {
      return users;
    }

    return [...users].sort((a, b) => {
      if (sortConfig.key === 'created_at') {
        // 登録日でソート
        if (a.created_at < b.created_at) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a.created_at > b.created_at) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      }
      return 0;
    });
  };

  const getMemoCheckData = () => {
    const checkData = checkDataArr.map((checkData, index) => {
      const memo = memoRefs.current[index].value;
      checkData.memo = memo;
      return checkData;
    });
    return checkData;
  };

  const saveAllMemoFunc = () => {
    const checkData = getMemoCheckData();
    saveAllMemoValue(checkData);
  };

  const getLastMonthMemosFunc = () => {
    const checkData = getMemoCheckData();
    getLastMonthMemos(checkData);
  };

  if (dataNullFlg === 0) {
    return (
      <>
        <p>この月はデータがありません。</p>
      </>
    );
  } else {
    return (
      <>
        <Box
          component="table"
          className={`${classes.tblStyle00} ${matches.xs ? classes.mobileTableFontSize : undefined}`}
          display={matches.xs && isCheckBox ? 'block' : undefined}
          whiteSpace={matches.xs && isCheckBox ? 'nowrap' : undefined}
        >
          <thead>
            <tr>
              {isCheckBox && (
                <th scope="col">
                  {invokedPath === '/all_users_save_kiroku'
                    ? '入力'
                    : invokedPath === '/life_csv_output'
                    ? '出力'
                    : '印刷'}
                  <br />
                  対象
                  <br />
                  <br />
                  <GeneralPrimaryCheckBox
                    checkBoxId={-1}
                    checked={allCheckData.selected}
                    indeterminate={allCheckData.indeterminate}
                    onChange={changeAllSelection}
                  />
                </th>
              )}
              <th scope="col">被保険者番号（介護）</th>
              <th scope="col">名前</th>
              <th scope="col">生年</th>
              <th scope="col">性別</th>
              {invokedPath === '/life_csv_output' && <th scope="col">介護度</th>}
              <th scope="col" className={classes.sortableHeader} onClick={() => requestSort('created_at')}>
                登録日▼
                {sortConfig && sortConfig.key === 'created_at' && (
                  <span className={classes.sortIcon}>{sortConfig.direction === 'ascending' ? '▲' : '▼'}</span>
                )}
              </th>
              {isMemoColumn ? (
                <>
                  <th scope="col">
                    メモ編集
                    <Box display="flex">
                      {invokedPath === '/all_users_print_press' || invokedPath === '/all_users_print_moff_check' ? (
                        <GeneralPrimaryButton
                          txt="メモを保存"
                          setCss={'margin: 5px auto 0px; width: 105px; height: 40px; font-size: 14px !important;'}
                          onClick={saveAllMemoFunc}
                        />
                      ) : null}
                      {invokedPath === '/all_users_print_press' ? (
                        <GeneralPrimaryButton
                          txt={'前月のメモ\n一括反映'}
                          setCss={
                            'margin: 5px auto 0px; width: 105px; height: 40px; font-size: 14px !important; white-space: pre; font-weight: normal;  font-size: 13.5px; line-height: 1.25;'
                          }
                          onClick={getLastMonthMemosFunc}
                        />
                      ) : null}
                    </Box>
                  </th>
                </>
              ) : null}
              {isReportCheckColumn ? <th scope="col">レポート確認</th> : null}
            </tr>
          </thead>

          <tbody>
            {getSortedUsers().map((user: UserListFieldConst.User, index: number) => {
              // データの有無に基づいてクラスを決定
              const hasData = invokedPath === '/life_csv_output' && usersWithData[user.user_id];
              // 利用者情報タブで、basicInfoに登録されていないユーザーかどうかをチェック
              const isNotInBasicInfo =
                isUserInfoTab && basicInfoUsers.length > 0 && !basicInfoUsers.includes(user.user_id);
              const rowClassName = `${
                isCheckBox && checkDataArr[index].selected ? classes.isPrintRow : classes.isNotPrintRow
              } ${hasData ? classes.hasDataRow : ''} ${isNotInBasicInfo ? classes.disabledRow : ''}`;

              return (
                <tr key={index} className={rowClassName}>
                  {isCheckBox && (
                    <td>
                      <GeneralPrimaryCheckBox
                        checkBoxId={index}
                        checked={checkDataArr[index].selected}
                        onChange={() => !isNotInBasicInfo && changeSelection(index)}
                        disabled={isNotInBasicInfo}
                      />
                    </td>
                  )}
                  <td>{user.user_care_id}</td>
                  <td>{user.user_name}</td>
                  <td>{user.user_birth_year}</td>
                  <td>{user.user_gender === 0 ? UserListField.Male : UserListField.Female}</td>
                  {invokedPath === '/life_csv_output' && (
                    <td>
                      {String(user.user_care_level) === '0'
                        ? '要支援1'
                        : String(user.user_care_level) === '1'
                        ? '要支援2'
                        : String(user.user_care_level) === '2'
                        ? '要介護1'
                        : String(user.user_care_level) === '3'
                        ? '要介護2'
                        : String(user.user_care_level) === '4'
                        ? '要介護3'
                        : String(user.user_care_level) === '5'
                        ? '要介護4'
                        : String(user.user_care_level) === '6'
                        ? '要介護5'
                        : String(user.user_care_level) === '906'
                        ? '事業対象者'
                        : String(user.user_care_level) === '999'
                        ? '要介護なし'
                        : ''}
                    </td>
                  )}
                  <td>{user.created_at}</td>
                  {isMemoColumn ? (
                    <>
                      <td>
                        <Box display="block" displayPrint="none">
                          <GeneralPrimaryTextArea
                            name={`text_area_${index}`}
                            rows={5}
                            cols={30}
                            placeholder={checkDataArr[index].selected ? textFormPlaceholder : ''}
                            maxLength={textFormMaxLength}
                            disabled={!checkDataArr[index].selected}
                            ref={(el: HTMLTextAreaElement) => (memoRefs.current[index] = el)}
                          />
                        </Box>
                      </td>
                    </>
                  ) : null}
                  {isReportCheckColumn ? (
                    <>
                      <td>
                        <GeneralPrimaryButton
                          txt={
                            <>
                              {currentMonth}
                              <br />
                              レポート確認
                            </>
                          }
                          setCss={'width: 110px; height: 60px; font-size: 13px !important; display: inline;'}
                          onClick={() => reportOutputButtonOnClick(user, currentMonth)}
                        />
                      </td>
                    </>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </Box>
        {(() => {
          return null;
        })()}
      </>
    );
  }
};

export default memo(forwardRef(UserList), (prevProps, nextProps) => {
  if (prevProps.users === nextProps.users) {
    return true;
  } else {
    return false;
  }
});
